import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { api } from '../services/api';
import { useSettingsStore } from '../store';
import { usePaymentTester } from '../hooks/usePaymentTester';

const BRAND_GRADIENT = 'linear-gradient(90deg, #5370F5, #3657F0)';

// Иконки (heroicons outline, MIT) в цвете бренда
const BLUE = '#3657F0';
const FlameIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
    <path d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
  </svg>
);
const BoltIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
  </svg>
);
const LockIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
  </svg>
);
const StarIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
  </svg>
);

// Пополнение Steam: клиент указывает логин и сумму зачисления (₽),
// платит сумму с наценкой через Cardlink P2P.
export const SteamTopupPage: React.FC = () => {
  const navigate = useNavigate();
  const { settings } = useSettingsStore();

  const markup = typeof settings.steamTopupMarkup === 'number' ? settings.steamTopupMarkup : 10;
  const min = typeof settings.steamTopupMin === 'number' ? settings.steamTopupMin : 100;
  const max = typeof settings.steamTopupMax === 'number' ? settings.steamTopupMax : 50000;
  // Тестерам фича доступна и при выключенном флаге (тестовый режим в проде)
  const isTester = usePaymentTester();
  const enabled = settings.steamTopupEnabled !== false || isTester;

  const [login, setLogin] = useState('');
  const [amountRaw, setAmountRaw] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showLoginHint, setShowLoginHint] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const amount = parseInt(amountRaw, 10) || 0;
  // К оплате: сумма + наценка, округление вверх до 5 ₽ (совпадает с бэкендом).
  // Совпадает с backend computeSteamPayAmount: сперва округление до копеек —
  // флоат-хвост (100 × 1.1 = 110.00000000000001) утаскивал ceil на 115 вместо 110.
  const payAmount = useMemo(
    () => (amount > 0 ? Math.ceil(Math.round(amount * (100 + markup)) / 100 / 5) * 5 : 0),
    [amount, markup],
  );

  const openSteamAccount = () => {
    const url = 'https://store.steampowered.com/login/?redir=account%2F&redir_ssl=1';
    const tg = (window as unknown as { Telegram?: { WebApp?: { openLink?: (u: string) => void } } }).Telegram?.WebApp;
    if (tg?.openLink) tg.openLink(url);
    else window.open(url, '_blank');
  };

  const fmt = (n: number) => n.toLocaleString('ru-RU');
  const amountInvalid = amount > 0 && (amount < min || amount > max);
  const canSubmit = enabled && !isProcessing && login.trim().length > 0 && amount >= min && amount <= max;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsProcessing(true);
    setError('');
    try {
      const order = await api.steamTopup(login.trim(), amount);
      // Ссылку на оплату присылает бот — просто закрываем mini app (как корзина)
      if ((order as any).useBotFlow) {
        try {
          (window as any).Telegram?.WebApp?.close?.();
        } catch { /* ignore */ }
        return;
      }
      navigate(`/payment/${order.id}`, { state: { paymentUrl: order.paymentUrl || '' } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать заказ');
    } finally {
      setIsProcessing(false);
    }
  };

  const faq: Array<{ q: string; a: string }> = [
    {
      q: 'Какие страны можно пополнить?',
      a: 'Пополнение возможно для стран СНГ, которые не входят в Евросоюз.\n\nОсновной список стран СНГ, доступных для пополнения:\n\nРеспублика Армения, Республика Беларусь, Республика Казахстан, Киргизская Республика, Республика Молдова, Российская Федерация, Республика Таджикистан и Республика Узбекистан, Грузия, Азербайджан, Украина.',
    },
    {
      q: 'Лимиты и ограничения',
      a: 'Ограничения на минимальные суммы для всех способов зачисления — 100 рублей.\nЛимит на максимальное пополнение баланса Steam на один логин за 24 часа — равен 500$.\n\nВнимание: ограничение распространяется на все способы пополнения. Если вы где-то уже пополнили ваш Steam на сумму 500$, наше пополнение может не пройти в связи с лимитом, будьте внимательны! Так же нельзя пополнять аккаунты с блокировкой (Красная табличка / КТ).',
    },
    {
      q: 'У меня новый аккаунт / первое пополнение',
      a: 'Если совершаете первое пополнение аккаунта Steam, то валюта аккаунта может смениться на одну из списка ($, ₸, € и другие). Страна аккаунта тоже будет изменена автоматически.\n\nЧтобы этого избежать и сохранить российский регион у аккаунта, перед пополнением добавьте в библиотеку Steam любую бесплатную игру, а лучше несколько (например: PUBG). Все это нужно делать под русским IP-адресом!',
    },
    {
      q: 'Пришла сумма меньше',
      a: 'Если валюта кошелька не рубль, зачисление проходит по курсу платежной системы — итоговая сумма может немного отличаться от указанной.',
    },
    {
      q: 'Не пришли деньги на кошелек',
      a: 'Обычно зачисление занимает 1–5 минут. Если денег нет дольше 30 минут — напишите в поддержку и приложите номер заказа, разберемся быстро.',
    },
    {
      q: 'Возврат средств',
      a: 'Если зачисление не удалось, платеж возвращается автоматически. По вопросам возврата напишите в поддержку — поможем.',
    },
  ];

  const card = 'bg-white rounded-2xl border border-gray-200/80';
  const inputClass =
    'w-full px-4 py-3.5 rounded-2xl bg-white border border-[#E1E3E5] text-sm text-text-primary placeholder:text-text-tertiary outline-none transition-colors focus:ring-2 focus:ring-primary/30';

  return (
    <Layout>
      <div className="px-4 pt-6 pb-8">
        {/* Hero */}
        <div className="flex flex-col items-center text-center mb-4">
          <img
            src="/services/logo-steam.webp"
            alt="Steam"
            className="mb-4"
            style={{ width: 88, height: 88, borderRadius: '50%', objectFit: 'cover' }}
          />
          <h1 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '28px', fontWeight: 800, lineHeight: '120%', letterSpacing: '-0.4px', color: '#1A1D1A' }}>
            Пополнение{' '}
            <span style={{ background: BRAND_GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Steam</span>
          </h1>
          <p className="mt-1" style={{ fontSize: '14px', lineHeight: '18px', color: '#818384' }}>
            Моментально · Без переплат · 24/7
          </p>
          <div
            className="mt-3 px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5"
            style={{ background: '#EEF1FE', border: '1px solid #D7DEFC' }}
          >
            <FlameIcon />
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#3657F0' }}>
              Комиссия всего {markup}% — ниже рынка
            </span>
          </div>
        </div>

        {!enabled ? (
          <div className={`${card} p-4`}>
            <p className="text-sm" style={{ color: '#818384' }}>
              Пополнение Steam временно недоступно. Загляните позже.
            </p>
          </div>
        ) : (
          <>
            {/* Steps */}
            <div className={`${card} p-4 mb-4`}>
              {[
                { n: '1', t: 'Укажите логин Steam', d: 'логин для входа в аккаунт — не никнейм' },
                { n: '2', t: 'Выберите сумму', d: `от ${fmt(min)} до ${fmt(max)} ₽ — пресет или своя` },
                { n: '3', t: 'Оплатите по СБП', d: 'деньги зачислятся на кошелек автоматически' },
              ].map((s, i) => (
                <div key={s.n} className={`flex items-start gap-3 ${i < 2 ? 'mb-3.5' : ''}`}>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ border: '2px solid #3657F0', color: '#3657F0', fontSize: '13px', fontWeight: 800 }}
                  >
                    {s.n}
                  </div>
                  <div>
                    <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '15px', fontWeight: 700, lineHeight: '20px', color: '#1A1D1A' }}>{s.t}</p>
                    <p style={{ fontSize: '13px', lineHeight: '17px', color: '#818384' }}>{s.d}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Login */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold uppercase tracking-wider" style={{ color: '#818384' }}>Логин Steam</label>
                <button
                  onClick={() => setShowLoginHint(!showLoginHint)}
                  className="px-3 py-1.5 rounded-xl text-xs font-semibold"
                  style={{ background: '#EEF1FE', color: '#3657F0' }}
                >
                  Где взять логин?
                </button>
              </div>
              <input
                type="text"
                autoComplete="off"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="Логин для входа (не никнейм!)"
                className={inputClass}
              />
              {showLoginHint && (
                <div className="mt-2 p-3 rounded-xl" style={{ background: '#EEF1FE' }}>
                  <p style={{ fontSize: '13px', lineHeight: '18px', color: '#1A1D1A' }}>
                    <span style={{ fontWeight: 700 }}>Обратите внимание!</span>
                    <br />
                    Логин — это то, что Вы указываете при входе в Steam. Если введете неверный логин, то средства уйдут другому пользователю.
                    <br /><br />
                    Посмотреть логин можно{' '}
                    <span
                      onClick={openSteamAccount}
                      style={{ color: '#3657F0', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      в настройках аккаунта Steam
                    </span>.
                  </p>
                </div>
              )}
            </div>

            {/* Amount */}
            <div className="mb-4">
              <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: '#818384' }}>Сумма пополнения</label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {[500, 1000, 2000, 5000].filter((v) => v >= min && v <= max).map((v) => (
                  <button
                    key={v}
                    onClick={() => setAmountRaw(String(v))}
                    className="py-3 rounded-2xl text-[15px] font-bold border transition-colors"
                    style={amount === v
                      ? { background: BRAND_GRADIENT, color: '#FFFFFF', borderColor: 'transparent' }
                      : { background: '#FFFFFF', color: '#1A1D1A', borderColor: '#E1E3E5' }}
                  >
                    {fmt(v)} ₽
                  </button>
                ))}
              </div>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={amountRaw}
                onChange={(e) => { if (/^\d*$/.test(e.target.value)) setAmountRaw(e.target.value); }}
                placeholder={`Своя сумма: ${fmt(min)}–${fmt(max)} ₽`}
                className={`${inputClass} ${amountInvalid ? 'ring-2 ring-red-400' : ''}`}
              />
              {amountInvalid && (
                <p className="text-xs text-red-500 mt-1">Сумма пополнения: от {fmt(min)} до {fmt(max)} ₽</p>
              )}
            </div>

            {/* Summary */}
            <div className={`${card} p-4 mb-4 flex items-center justify-between`}>
              <div>
                <p style={{ fontSize: '13px', color: '#818384' }}>На кошелек Steam</p>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 800, color: '#1A1D1A' }}>
                  {amount > 0 && !amountInvalid ? `${fmt(amount)} ₽` : '—'}
                </p>
              </div>
              <div className="text-right">
                <p style={{ fontSize: '13px', color: '#818384' }}>К оплате по СБП · +{markup}%</p>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 800, background: BRAND_GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {amount > 0 && !amountInvalid ? `${fmt(payAmount)} ₽` : `от ${fmt(min)} до ${fmt(max)} ₽`}
                </p>
              </div>
            </div>

            {error && <p className="text-sm text-red-500 mb-3">{error}</p>}

            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full py-4 rounded-2xl text-white font-bold text-base disabled:opacity-40 active:scale-[0.99] transition"
              style={{ background: BRAND_GRADIENT }}
            >
              {isProcessing ? 'Создаем заказ…' : 'Пополнить'}
            </button>

            <p className="text-center mt-3" style={{ fontSize: '12px', lineHeight: '16px', color: '#818384' }}>
              Нажимая «Пополнить», вы принимаете условия{' '}
              <span className="underline font-semibold cursor-pointer" style={{ color: '#3657F0' }} onClick={() => navigate('/terms')}>Пользовательского соглашения</span>
              {' '}и{' '}
              <span className="underline font-semibold cursor-pointer" style={{ color: '#3657F0' }} onClick={() => navigate('/privacy')}>Политики конфиденциальности</span>
            </p>

            <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
              {[
                { icon: <BoltIcon />, t: 'Моментально' },
                { icon: <LockIcon />, t: 'Оплата по СБП' },
                { icon: <StarIcon />, t: '5 000+ отзывов' },
              ].map((b) => (
                <span key={b.t} className="inline-flex items-center gap-1.5" style={{ fontSize: '13px', fontWeight: 600, color: '#1A1D1A' }}>
                  {b.icon}
                  {b.t}
                </span>
              ))}
            </div>
            <p className="text-center mt-2" style={{ fontSize: '12px', lineHeight: '16px', color: '#818384' }}>
              Если валюта вашего кошелька Steam не рубль, сумма зачислится в валюте кошелька по курсу платежной системы.
            </p>

            {/* FAQ */}
            <h2 className="mt-6 mb-3" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '20px', fontWeight: 800, color: '#1A1D1A' }}>
              Частые вопросы
            </h2>
            <div className="space-y-2">
              {faq.map((f, i) => (
                <div key={i} className={`${card} overflow-hidden`}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full px-4 py-3.5 flex items-center justify-between text-left"
                  >
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#1A1D1A' }}>{f.q}</span>
                    <span style={{ fontSize: '20px', fontWeight: 600, color: '#3657F0', transform: openFaq === i ? 'rotate(45deg)' : 'none', transition: 'transform 0.15s' }}>+</span>
                  </button>
                  {openFaq === i && (
                    <p className="px-4 pb-3.5" style={{ fontSize: '14px', lineHeight: '19px', color: '#818384', whiteSpace: 'pre-line' }}>{f.a}</p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};
